import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";
import { Button } from "./Button";
import { Portal } from "./Portal";

export interface ModalAction {
  label: string | React.ReactNode;
  color?:
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "light"
  | "dark";
  outline?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  show?: boolean;
  type?: "button" | "submit" | "reset";
  form?: string;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  icon?: React.ElementType;
  iconVariant?: "blue" | "red" | "amber" | "green";
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "full";
  closeOnBackdropClick?: boolean;
  actions?: ModalAction[];
  showFooter?: boolean;
  hideActions?: boolean;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  bodyStyle?: React.CSSProperties;
  headerActions?: React.ReactNode;
  zIndex?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  icon: Icon,
  iconVariant = "blue",
  children,
  size = "md",
  closeOnBackdropClick = true,
  actions = [],
  showFooter = true,
  hideActions = false,
  className = "",
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
  bodyStyle = {},
  headerActions,
  zIndex,
}) => {
  // `visible` controls whether the DOM element is mounted
  // `animating` drives the CSS transition classes
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      // Trigger enter animation on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
    } else {
      // Trigger leave animation, then unmount
      setAnimating(false);
      const timer = setTimeout(() => setVisible(false), 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!visible) return null;

  const sizeClasses = {
    xs: "max-w-xs",
    sm: "max-w-sm max-h-[90vh]",
    md: "max-w-md max-h-[90vh]",
    lg: "max-w-lg max-h-[90vh]",
    xl: "max-w-xl max-h-[90vh]",
    "2xl": "max-w-2xl max-h-[90vh]",
    "3xl": "max-w-3xl max-h-[90vh]",
    "4xl": "max-w-4xl max-h-[90vh]",
    "5xl": "max-w-5xl max-h-[90vh]",
    full: "max-w-[95vw] max-h-[95vh]",
  };

  const getButtonVariant = (action: ModalAction) => {
    if (action.outline) return "outline";
    if (action.color === "danger") return "destructive";
    if (action.color === "secondary") return "secondary";
    if (action.color === "success") return "success";
    return "primary";
  };

  const iconVariantClasses = {
    blue: { bg: "bg-blue-100 dark:bg-blue-700/30", icon: "text-blue-600 dark:text-blue-400" },
    red: { bg: "bg-red-100 dark:bg-red-700/30", icon: "text-red-600 dark:text-red-400" },
    amber: { bg: "bg-amber-100 dark:bg-amber-700/30", icon: "text-amber-600 dark:text-amber-400" },
    green: { bg: "bg-green-100 dark:bg-green-700/30", icon: "text-green-600 dark:text-green-400" },
  };
  const iconStyle = iconVariantClasses[iconVariant];

  return (
    <Portal>
      <div className={cn("fixed inset-0 flex items-center justify-center mb-0", zIndex || "z-[100]")}>
        {/* Backdrop */}
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-250 ease-in-out",
            "bg-black/20 dark:bg-black/60 backdrop-blur-[2px]",
            animating ? "opacity-100" : "opacity-0",
          )}
          onClick={closeOnBackdropClick ? onClose : undefined}
        />

        {/* Modal Panel */}
        <div
          className={cn(
            "relative bg-white dark:bg-gray-900 rounded-sm shadow-2xl w-full mx-4 flex flex-col overflow-hidden",
            "border border-gray-200 dark:border-gray-700",
            "transition-all duration-250 ease-in-out",
            animating
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-95 translate-y-4",
            sizeClasses[size],
            className,
          )}
        >
          {/* Header */}
          {(title || description || Icon) && (
            <div
              className={cn(
                "flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 rounded-t-sm",
                headerClassName,
              )}
            >
              <div className="flex gap-4 items-start pr-4">
                {Icon && (
                  <div className={cn("w-10 h-10 rounded-sm flex items-center justify-center shrink-0", iconStyle.bg)}>
                    <Icon className={cn("w-5 h-5", iconStyle.icon)} />
                  </div>
                )}
                <div className="flex flex-col justify-center min-h-[40px]">
                  {title && (
                    <h2 className={cn("text-base font-semibold text-gray-900 dark:text-white leading-tight", !description && "mt-0")}>
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                      {description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {headerActions}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-sm cursor-pointer text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary -mr-2"
                  aria-label="Close modal"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Body */}
          <div
            className={cn(
              "p-4 overflow-y-auto custom-scrollbar overflow-x-hidden",
              bodyClassName,
            )}
            style={{
              maxHeight: 'calc(80vh - 140px)',
              ...bodyStyle
            }}
          >
            {children}
          </div>

          {/* Footer */}
          {showFooter && (
            <div
              className={cn(
                "px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 shrink-0",
                footerClassName,
              )}
            >
              {actions.length === 0 && !hideActions ? (
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              ) : (
                actions.map(
                  (action, index) =>
                    action.show !== false && (
                      <Button
                        key={index}
                        variant={getButtonVariant(action)}
                        disabled={action.disabled}
                        loading={action.loading}
                        onClick={action.onClick}
                        type={action.type}
                        form={action.form}
                      >
                        {action.label}
                      </Button>
                    ),
                )
              )}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default Modal;
